import { useContractRead } from 'wagmi'
import { formatEther } from 'viem'
import { ADDRESSES, CONFIG, TokenAddress, TOKEN_NAMES, CHAIN_ID, TOKEN_SYMBOLS } from '../config'
import { motion } from 'framer-motion'
import { useNetwork } from 'wagmi'
import { Abi } from 'viem'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'
import { useMemo } from 'react'
import { useProduct } from '../contexts/ProductContext'

// Utility function to format numbers with commas
const formatTokenAmount = (amount: bigint): string => {
  const formatted = formatEther(amount);
  const [whole, decimal] = formatted.split('.');
  const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return decimal ? `${withCommas}.${decimal}` : withCommas;
};

// Array of colors for different lines
const CHART_COLORS = [
  '#8B5CF6', // Violet
  '#6366F1', // Indigo
  '#A855F7', // Purple
  '#D946EF', // Fuchsia
  '#EC4899', // Pink
  '#F472B6', // Rose
]

// Generate mock historical data (will be replaced with real data later)
const generateMockData = (assets: { symbol: string, balance: bigint }[]) => {
  const dates = Array.from({ length: 30 }, (_, i) => {
    const date = new Date()
    date.setDate(date.getDate() - (29 - i))
    return date.toISOString().split('T')[0]
  })

  return dates.map(date => {
    const dataPoint: { [key: string]: any } = { date }
    assets.forEach(asset => {
      dataPoint[asset.symbol] = Number(formatEther(asset.balance))
    })
    return dataPoint
  })
}

export function SystemStatus() {
  const { chain } = useNetwork();
  const { selectedProduct } = useProduct();
  
  // If no product is selected, show a message
  if (!selectedProduct?.address) {
    return (
      <div className="max-w-4xl mx-auto p-8">
        <h1 className="text-4xl font-bold mb-3 bg-gradient-to-r from-blue-500/90 via-purple-500/90 to-pink-500/90 text-transparent bg-clip-text">System Status</h1>
        <div className="text-center">
          <p className="text-secondary">Please select a product from the products page to view its status.</p>
        </div>
      </div>
    );
  }

  // Get supported assets first
  const { data: supportedAssets = [], isLoading: isLoadingAssets } = useContractRead({
    address: selectedProduct.address,
    abi: CONFIG.TRINITY_BOND_ABI.abi as Abi,
    functionName: 'getSupportedAssets',
    enabled: chain?.id === CHAIN_ID && !!selectedProduct?.address,
  }) as { data: `0x${string}`[], isLoading: boolean };

  // Get balances for all supported assets using direct balanceOf calls
  const assetBalances = supportedAssets.map(asset => {
    const { data: balance = 0n } = useContractRead({
      address: asset,
      abi: CONFIG.ERC20_ABI.abi as Abi,
      functionName: 'balanceOf',
      args: [selectedProduct.address],
      enabled: chain?.id === CHAIN_ID && !!selectedProduct?.address,
    }) as { data: bigint };
    return balance;
  });

  // Get symbols for all supported assets
  const { data: symbols = [] } = useContractRead({
    address: selectedProduct.address,
    abi: CONFIG.TRINITY_BOND_ABI.abi as Abi,
    functionName: 'getAssetSymbols',
    args: [supportedAssets],
    enabled: chain?.id === CHAIN_ID && supportedAssets.length > 0 && !!selectedProduct?.address,
  }) as { data: string[] };

  // Get debts for all supported assets
  const { data: debts = [] } = useContractRead({
    address: selectedProduct.address,
    abi: CONFIG.TRINITY_BOND_ABI.abi as Abi,
    functionName: 'getAssetDebts',
    enabled: chain?.id === CHAIN_ID && supportedAssets.length > 0 && !!selectedProduct?.address,
  }) as { data: { asset: `0x${string}`, amount: bigint }[] };

  // Get Trinity token balance in treasury
  const { data: trinityBalance = 0n, isLoading: isTrinityLoading } = useContractRead({
    address: ADDRESSES.TRINITY_TOKEN,
    abi: CONFIG.ERC20_ABI.abi as Abi,
    functionName: 'balanceOf',
    args: [selectedProduct.address],
    enabled: chain?.id === CHAIN_ID && !!selectedProduct?.address,
  }) as { data: bigint, isLoading: boolean };

  // Get Coupon token balance in treasury
  const { data: couponBalance = 0n, isLoading: isCouponLoading } = useContractRead({
    address: ADDRESSES.COUPON_TOKEN,
    abi: CONFIG.ERC20_ABI.abi as Abi,
    functionName: 'balanceOf',
    args: [selectedProduct.address],
    enabled: chain?.id === CHAIN_ID && !!selectedProduct?.address,
  }) as { data: bigint, isLoading: boolean };

  // Process the results using useMemo to avoid unnecessary recalculations
  const { treasuryAssets, assetDebts, filteredDebts } = useMemo(() => {
    const treasuryAssets = supportedAssets.map((address, index) => ({
      address,
      symbol: symbols[index] || TOKEN_SYMBOLS[address as TokenAddress] || `Asset ${index + 1}`,
      balance: assetBalances[index] || 0n
    }));

    const assetDebts = supportedAssets.map((address, index) => ({
      asset: address,
      debt: debts[index]?.amount || 0n
    }));

    const filteredDebts = assetDebts.filter(debt => debt.debt > 0n);

    return { treasuryAssets, assetDebts, filteredDebts };
  }, [supportedAssets, symbols, assetBalances, debts]);

  // Generate chart data
  const chartData = useMemo(() => generateMockData(treasuryAssets), [treasuryAssets]);

  if (isLoadingAssets) {
    return (
      <div className="max-w-4xl mx-auto p-8">
        <h1 className="text-4xl font-bold mb-3 bg-gradient-to-r from-blue-500/90 via-purple-500/90 to-pink-500/90 text-transparent bg-clip-text">System Status</h1>
        <div className="text-center">
          <p className="text-secondary">Loading system status...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-8 p-8">
      {/* Product Address Section */}
      <h1 className="text-4xl font-bold mb-3 bg-gradient-to-r from-blue-500/90 via-purple-500/90 to-pink-500/90 text-transparent bg-clip-text">System Status</h1>
      <h2 className="text-2xl font-bold mb-8">{selectedProduct.name}</h2>
      <div className="glass-panel p-4 rounded-lg">
          <div className="text-sm font-mono break-all">
            Contract Address: {selectedProduct.address}
          </div>
        </div>
      {/* System Debts Section */}
      <motion.div
        className="relative overflow-hidden rounded-2xl p-8 bg-gradient-to-br from-rose-500/20 via-red-500/20 to-orange-500/20"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h2 className="text-2xl font-bold mb-8">System Debts</h2>

        {filteredDebts.length > 0 ? (
          <div className="space-y-4">
            {filteredDebts.map((debt, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="glass-panel p-6 rounded-lg"
              >
                <div className="flex justify-between items-center">
                  <div className="font-medium">{TOKEN_NAMES[debt.asset as TokenAddress] || 'Unknown'}</div>
                  <div>{formatTokenAmount(debt.debt)}</div>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <p className="text-center text-secondary py-4">No system debts found</p>
        )}
      </motion.div>

      {/* Treasury Assets Section */}
      <motion.div
        className="relative overflow-hidden rounded-2xl p-8 bg-gradient-to-br from-indigo-500/20 via-purple-500/20 to-violet-500/20"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <h2 className="text-2xl font-bold mb-8">Treasury Assets</h2>

        {treasuryAssets.length > 0 ? (
          <>
            {/* Chart Section */}
            <div className="glass-panel p-6 rounded-lg mb-8 bg-gradient-to-br from-indigo-500/5 to-violet-500/5">
              <div className="h-[400px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                    <XAxis 
                      dataKey="date" 
                      stroke="currentColor" 
                      opacity={0.5}
                      tick={{ fontSize: 12 }}
                    />
                    <YAxis 
                      stroke="currentColor"
                      opacity={0.5}
                      tick={{ fontSize: 12 }}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'rgba(15, 23, 42, 0.8)',
                        border: 'none',
                        borderRadius: '8px',
                        color: '#f8fafc'
                      }}
                    />
                    <Legend />
                    {treasuryAssets.map((asset, index) => (
                      <Line
                        key={asset.symbol}
                        type="monotone"
                        dataKey={asset.symbol}
                        stroke={CHART_COLORS[index % CHART_COLORS.length]}
                        strokeWidth={2}
                        dot={false}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Assets Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {treasuryAssets.map((asset, index) => (
                <motion.div
                  key={asset.address}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="glass-panel p-6 rounded-lg relative overflow-hidden"
                >
                  <div 
                    className="absolute inset-0 opacity-10" 
                    style={{ 
                      background: `linear-gradient(135deg, ${CHART_COLORS[index % CHART_COLORS.length]}40 0%, transparent 100%)` 
                    }} 
                  />
                  <div className="relative z-10 flex flex-col h-full">
                    <div className="text-lg font-medium mb-1">
                      {TOKEN_NAMES[asset.address as TokenAddress] || 'Unknown'}
                    </div>
                    <div className="text-sm text-gray-500 mb-4">
                      {asset.symbol}
                    </div>
                    <div className="mt-auto">
                      <div className="text-3xl font-bold">
                        {formatTokenAmount(asset.balance)}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}

              {/* Trinity and Coupon tokens */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="glass-panel p-6 rounded-lg relative overflow-hidden md:col-span-2"
              >
                <div className="absolute inset-0 opacity-10 bg-gradient-to-br from-amber-500 via-orange-500 to-yellow-500" />
                <div className="relative z-10 flex flex-col h-full">
                  <div className="text-lg font-medium mb-1">
                    {TOKEN_NAMES[ADDRESSES.TRINITY_TOKEN]}
                  </div>
                  <div className="text-sm text-gray-500 mb-4">
                    {TOKEN_SYMBOLS[ADDRESSES.TRINITY_TOKEN]}
                  </div>
                  <div className="mt-auto">
                    <div className="text-3xl font-bold">
                      {!isTrinityLoading ? formatTokenAmount(trinityBalance) : '...'} †
                    </div>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="glass-panel p-6 rounded-lg relative overflow-hidden"
              >
                <div className="absolute inset-0 opacity-10 bg-gradient-to-br from-amber-500 via-orange-500 to-yellow-500" />
                <div className="relative z-10 flex flex-col h-full">
                  <div className="text-lg font-medium mb-1">
                    {TOKEN_NAMES[ADDRESSES.COUPON_TOKEN]}
                  </div>
                  <div className="text-sm text-gray-500 mb-4">
                    {TOKEN_SYMBOLS[ADDRESSES.COUPON_TOKEN]}
                  </div>
                  <div className="mt-auto">
                    <div className="text-3xl font-bold">
                      {!isCouponLoading ? formatTokenAmount(couponBalance) : '...'} †
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </>
        ) : (
          <p className="text-center text-secondary py-4">No treasury assets found</p>
        )}
      </motion.div>
    </div>
  );
} 